import { gql, useQuery } from "@apollo/client";
import { Disclosure } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  Modal,
  ReplacementServiceOrderPrepaymentView,
  ReplacementServiceOrderResolutionView,
  ReplacementServiceOrderView,
  ServiceOrderHistoryView,
} from "components";
import { Action } from "components/buttons/action-button";
import { useMemo, useState } from "react";
import { useNavigate, useSearch } from "react-location";
import { Tooltip } from "react-tooltip";
import { LocationGenerics } from "router/location";
import { classNames, wrapClick } from "utils";

export const GET_SERVICE_ORDER = gql`
  fragment ServiceOrderPrepaidSync on PrepaidSync {
    _id
    status
    stage
    completedAt
    data {
      servicePointCode
      regionCode
      customer {
        surname
        otherNames
        idNo
        idType
        taxRefNo
        address1
        address2
        address3
        telephone1
        telephone2
        telephone3
        fax
        email
      }
      servicePoint {
        address1
        address2
        address3
        geoCode
        digitalAddress
      }
      tariffClass {
        tariffCode
        tariffGroup
      }
      meter {
        meterSerialNo
        batchNo
        make
        model
        phase
        systemDetails
      }
      debt {
        debtType
        debtAmount
        debtRef
        instalmentDueDate
        debtStatus
      }
    }
    history {
      action
      data
      message
      timestamp
      status
    }
  }

  fragment MeterDetail on Meter {
    _id
    code
    modelMeta {
      brandCode
      brandName
      systemCode
      systemName
      modelCode
      modelName
      phase
    }
    syncMethod
  }

  query GetReplacementServiceOrder($id: ID!) {
    replacementServiceOrder: getReplacementServiceOrder(id: $id) {
      _id
      code
      region {
        _id
        code
        name
      }
      district {
        _id
        code
        name
      }
      contactPerson {
        fullName
        phoneNumber
      }
      location {
        address
        community
      }
      farmingOutContractor {
        _id
        code
        name
        contactPerson {
          fullName
          phoneNumber
          emailAddress
        }
      }
      farmingOutContractorUser {
        _id
        code
        username
        firstName
        lastName
        phoneNumber
        emailAddress
      }
      contractorAssigner {
        _id
        lastName
        firstName
        phoneNumber
        emailAddress
        profileImageUrl
      }
      replacementReason
      resolution {
        property {
          poleNumber
          propertyImageUrls
        }
        service {
          qrCode
          meter {
            ...MeterDetail
          }
        }
        reverseSyncMeter {
          ...MeterDetail
        }
        reverseSyncReading {
          readingDate
          readingValue
        }
        previousReading {
          readingDate
          readingValue
          readingImageUrl
        }
        currentReading {
          readingDate
          readingValue
          readingImageUrl
        }
        notes
        recoveryData {
          meterCondition
          remainingBalanceType
          remainingBalance
          debtAmount
        }
        materials {
          material {
            _id
            code
            name
            category
            quantityUnit
            quantity
            storeQuantity
            createdAt
            updatedAt
          }
          quantity
        }
        recoveredMaterials {
          material {
            _id
            code
            name
            category
            quantityUnit
            quantity
            storeQuantity
            createdAt
            updatedAt
          }
          quantity
        }
      }
      status
      priority
      history {
        actor {
          ... on Contractor {
            _id
            name
            code
          }
          ... on ContractorUser {
            _id
            firstName
            lastName
            username
            code
          }
          ... on MeterContractor {
            _id
            name
            code
          }
          ... on MeterContractorUser {
            _id
            firstName
            lastName
            code
          }
          ... on User {
            _id
            firstName
            lastName
            code
          }
        }
        assignee {
          ... on Contractor {
            _id
            name
            code
          }
          ... on ContractorUser {
            _id
            firstName
            lastName
            username
            code
          }
          ... on MeterContractor {
            _id
            name
            code
          }
          ... on MeterContractorUser {
            _id
            firstName
            lastName
            code
          }
          ... on User {
            _id
            firstName
            lastName
            code
          }
        }
        actorType
        assigneeType
        timestamp
        action
        notes
      }
      notes
      estimatedResolutionDate
      servicePoint {
        _id
        code
        geoCode
        tariffClass {
          _id
          code
          name
        }
      }
      requestedMeterPhase
      replacementType
      meter {
        ...MeterDetail
      }
      installationMeter {
        ...MeterDetail
      }
      installedMeter {
        ...MeterDetail
      }
      retiredMeter {
        ...MeterDetail
      }
      installationMeterSystemSyncStatus
      installationMeterSystemSyncMethod
      installationSync {
        ...ServiceOrderPrepaidSync
      }
      uninstallationSync {
        ...ServiceOrderPrepaidSync
      }
      presetAmountSync {
        ...ServiceOrderPrepaidSync
      }
      assignedAt
      contractorAssignedAt
      startedAt
      resolvedAt
      contractorResolvedAt
      completedAt
      rejectedAt
      createdAt
      reverseSyncedAt
      updatedAt
      rejectionReason
      result
      oldMeterNumber
      oldMeterPhase
    }
  }
`;

const orderTabs = [
  {
    name: "Order Details",
    href: "OrderDetails",
    activeStatues: [
      "AssignedToSupplier",
      "AssignedToInstaller",
      "Resolved",
      "Completed",
      "Disapproved",
      "Rejected",
      "InProgress",
    ],
  },
  {
    name: "Order History",
    href: "OrderHistory",
    activeStatues: [
      "AssignedToSupplier",
      "AssignedToInstaller",
      "Resolved",
      "Disapproved",
      "Rejected",
      "Completed",
      "InProgress",
    ],
  },
  {
    name: "Prepayment Details",
    href: "PrepaymentDetails",
    activeStatues: [
      "AssignedToInstaller",
      "Resolved",
      "InProgress",
      "Disapproved",
      "Completed",
    ],
  },
  {
    name: "Resolution Details",
    href: "ResolutionDetails",
    activeStatues: ["Resolved", "Disapproved", "Completed"],
  },
];

export default function ViewReplacementServiceOrderContainer({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  refetch?: () => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const [orderTab, setOrderTab] = useState("OrderDetails");
  const { data, loading, refetch } = useQuery(GET_SERVICE_ORDER, {
    variables: {
      id: searchParams.id,
    },
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
  });
  const dispatchAction =
    (action: Exclude<Action, "expand" | "goto" | "clone">) => () => {
      navigate({
        search: (old) => ({
          ...old,
          modal: action,
        }),
      });
    };
  const __setOrderTab = (tab: string) => () => {
    setOrderTab(tab);
  };

  const approveDisabled = useMemo(
    () =>
      data?.replacementServiceOrder?.installationMeterSystemSyncMethod ===
        "ReverseSync" && !data?.replacementServiceOrder?.reverseSyncedAt,
    [
      data?.replacementServiceOrder?.installationMeterSystemSyncMethod,
      data?.replacementServiceOrder?.reverseSyncedAt,
    ],
  );

  return (
    <Modal
      open={open}
      setOpen={() => {
        setOpen(false);
        navigate({
          search: (old) => ({
            ...old,
            modal: undefined,
            id: undefined,
          }),
        });
      }}
      hideActions={false}
      hidePadding={true}
      loading={loading}
      title="Service Order Information"
      size="4xl"
      description="Details of service order are shown below"
      renderActions={() => (
        <>
          {![
            "Pending",
            "InProgress",
            "Rejected",
            "Resolved",
            "Disapproved",
            "Completed",
          ].includes(data?.replacementServiceOrder?.status) &&
            !!data?.replacementServiceOrder?.status && (
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(dispatchAction("reset-order"))}
              >
                Reset
              </button>
            )}
          {["AssignedToSupplier", "AssignedToInstaller", "InProgress"].includes(
            data?.replacementServiceOrder?.status,
          ) && (
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={wrapClick(dispatchAction("reject"))}
            >
              Reject
            </button>
          )}
          {["AssignedToSupplier", "AssignedToInstaller", "InProgress"].includes(
            data?.replacementServiceOrder?.status,
          ) && (
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={wrapClick(dispatchAction("assign"))}
            >
              {data?.replacementServiceOrder?.status === "AssignedToSupplier"
                ? "Assign"
                : "Reassign"}
            </button>
          )}
          {["Resolved"].includes(data?.replacementServiceOrder?.status) && (
            <>
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(dispatchAction("disapprove"))}
              >
                Disapprove
              </button>
              <Tooltip id="approve-tooltip" />

              <button
                type="button"
                data-tooltip-delay-show={0}
                data-tooltip-id="approve-tooltip"
                data-tooltip-hidden={!approveDisabled}
                data-tooltip-html={`
                  <div className="flex items-center gap-x-6 bg-yellow-500 px-6 py-2.5">
                    <p className="text-sm leading-6 text-white">
                      Reverse Sync Information Is Not In Yet, Kindly Resolve Worktask In AMC
                    </p>
                  </div>
          `}
                className={classNames(
                  "w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm",
                )}
                onClick={wrapClick(dispatchAction("approve"))}
              >
                Approve
              </button>
            </>
          )}
        </>
      )}
    >
      {data?.replacementServiceOrder?._id && (
        <>
          {approveDisabled && (
            <Disclosure defaultOpen>
              <Disclosure.Panel
                as="div"
                className="flex items-center gap-x-6 bg-yellow-500 px-6 py-2.5 sm:px-3.5 sm:before:flex-1"
              >
                {({ close }) => (
                  <>
                    <p className="text-sm leading-6 text-white">
                      <strong className="font-semibold">
                        This Service Order Requires Resolution With AMC Mobile
                      </strong>
                    </p>
                    <div className="flex flex-1 justify-end">
                      <button
                        onClick={wrapClick(close)}
                        type="button"
                        className="-m-3 p-3 focus-visible:outline-offset-[-4px]"
                      >
                        <span className="sr-only">Dismiss</span>
                        <XMarkIcon
                          className="h-5 w-5 text-white"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </>
                )}
              </Disclosure.Panel>
            </Disclosure>
          )}
          {[
            "AssignedToInstaller",
            "InProgress",
            "Resolved",
            "Disapproved",
            "Rejected",
            "Completed",
          ].includes(data?.replacementServiceOrder?.status) && (
            <div className="block">
              <div className="border-b border-gray-200 bg-white px-6">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  {orderTabs.map((_orderTab) => (
                    <button
                      key={_orderTab.name}
                      onClick={wrapClick(__setOrderTab(_orderTab.href))}
                      disabled={
                        !_orderTab.activeStatues.includes(
                          data?.replacementServiceOrder?.status,
                        )
                      }
                      className={classNames(
                        orderTab === _orderTab.href
                          ? "border-primary-500 text-primary-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200",
                        "whitespace-nowrap disabled:cursor-not-allowed flex py-4 px-1 border-b-2 font-medium text-sm",
                      )}
                      aria-current={
                        orderTab === _orderTab.href ? "page" : undefined
                      }
                    >
                      {_orderTab.name}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}
          <div className="flex-1 w-full min-h-[65vh] max-h-[65vh] overflow-y-auto  sm:p-6">
            {orderTab === "OrderDetails" && (
              <ReplacementServiceOrderView
                replacementServiceOrder={data?.replacementServiceOrder}
              />
            )}
            {orderTab === "OrderHistory" && (
              <ServiceOrderHistoryView
                history={data?.replacementServiceOrder?.history}
              />
            )}
            {orderTab === "ResolutionDetails" && (
              <ReplacementServiceOrderResolutionView
                oldMeterNumber={data?.replacementServiceOrder?.oldMeterNumber}
                resolution={{
                  ...data?.replacementServiceOrder?.resolution,
                  installedMeter: data?.replacementServiceOrder?.installedMeter,
                  uninstalledMeter: data?.replacementServiceOrder?.meter,
                }}
                result={data?.replacementServiceOrder?.result}
              />
            )}
            {orderTab === "PrepaymentDetails" && (
              <ReplacementServiceOrderPrepaymentView
                serviceOrder={data?.replacementServiceOrder}
                refetch={() => {
                  void refetch();
                }}
              />
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
